import React, { forwardRef } from 'react';
import type { ForwardedRef } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

export interface EmptyStateProps {
  bg?: string;
  children?: React.ReactNode;
  contentFullWidth?: boolean;
  dataTestId?: string;
  height?: number | string;
  isOver: boolean;
  nbFinalChildren?: number | null;
  onClick?: () => void;
  onDoubleClick?: () => void;
  overColor?: string;
  overHeight?: number | string;
  p?: number;
  sectionHeight?: number | null;
  width?: number | string;
}

const EmptyState = forwardRef(
  (
    {
      bg = 'white',
      children,
      contentFullWidth = false,
      dataTestId,
      height: initialHeight = '100%',
      isOver,
      nbFinalChildren,
      onClick,
      onDoubleClick,
      overColor,
      overHeight: initialOverHeight,
      p = 4,
      sectionHeight,
      width = '100%',
      ...props
    }: EmptyStateProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const height =
      nbFinalChildren === 1 && sectionHeight ? sectionHeight : initialHeight;

    const overHeight =
      nbFinalChildren === 1 && sectionHeight
        ? sectionHeight
        : initialOverHeight;

    return (
      <StyledWrapper
        {...(onDoubleClick ? { onDoubleClick } : { onClick })}
        data-testid={dataTestId}
        ref={ref}
        height={height}
        p={2}
        width={width}
        {...(contentFullWidth &&
          isOver && {
            bg: overColor,
            height: overHeight || height,
          })}
        {...props}
      >
        {contentFullWidth ? (
          children
        ) : (
          <StyledContent
            bg={bg}
            alignItems="center"
            fontSize={2}
            justifyContent="center"
            flex={1}
            p={p}
            $isOver={isOver}
          >
            {children}
          </StyledContent>
        )}
      </StyledWrapper>
    );
  },
);

export default EmptyState;

const StyledContent = styled(Box)<{
  $isOver: boolean;
}>`
  border-radius: 3px;
  border: 1px dotted ${({ $isOver }) => ($isOver ? 'red' : 'black')};
`;

const StyledWrapper = styled(Box).attrs({
  fontFamily: 'Inter',
})`
  pointer-events: all;
  user-select: none;
`;
